import { createFileRoute, useNavigate } from '@tanstack/react-router';
import Button from 'components/Button/Button';
import useAppLanguage from 'hooks/useAppLanguage';
import React, { useEffect, useState } from 'react';
import {
  useActivateProfileMutation,
  useGetProfileQuery,
} from 'store/slices/profile-api';
import { useGetUserTermsQuery } from 'store/slices/termsAndConditions-api';
import { EVENTS, SIGNUP_STEPS, trackEvent } from 'utils/analytics';
import messages from '../../messages';
import { AcceptTermsAndConditionsState } from '../types/AcceptTermsAndConditionsState';
import { useRefreshTokenMutation } from 'app/auth/api/auth-api';
import { Loader, Markdown } from '@altify/components';
import { TERMS_ONBOARDING_MARKDOWN_STYLES } from 'app/_authenticated/trade/components/TermsModal/useTermsVisibility';

export const Route = createFileRoute('/auth/signup/_layout/tos')({
  component: AcceptTermsAndConditions,
});

function AcceptTermsAndConditions() {
  const navigate = useNavigate();

  const language = useAppLanguage();
  const acceptTermsAndConditionsMessages =
    messages[language].acceptTermsAndConditions;

  const { data: userProfileResponse } = useGetProfileQuery();
  const { data: termsList, isLoading: isTermsLoading } = useGetUserTermsQuery();
  const [terms, setTerms] = useState<NonNullable<typeof termsList>>([]);
  useEffect(() => {
    if (termsList && terms.length === 0) {
      setTerms(termsList.filter(t => !t.accepted && t.showOnRegistration));
    }
  }, [termsList, terms.length]);
  const [acceptTerms, acceptTermsResult] = useActivateProfileMutation();
  const [refreshToken] = useRefreshTokenMutation();

  const [state, setState] = useState<AcceptTermsAndConditionsState>({});
  const requiredTerms =
    terms
      .filter(
        term => !term.accepted && term.showOnRegistration && term.mandatory,
      )
      .map(term => term.code ?? '') ?? [];
  function isStateValid(): boolean {
    if (
      requiredTerms.length > 0 &&
      requiredTerms.some(term => !state.acceptedTerms?.includes(term))
    )
      return false;
    return true;
  }

  async function onContinuePress() {
    const result = await acceptTerms({
      revixRoundUp: state.acceptedTerms?.includes('WEEKLY_NEWSLETTER') ?? false,
      acceptedTerms: state.acceptedTerms ?? [],
    });
    if (!result.error) {
      await refreshToken();
      trackEvent(EVENTS.REGISTER, {
        step: SIGNUP_STEPS.TERMS,
        userId: userProfileResponse?.id ?? undefined,
      });
      navigate({ to: '/auth/signup/welcome' });
    }
  }

  return (
    <>
      {isTermsLoading ? (
        <Loader />
      ) : (
        <div className="bg-ghost-white m-4">
          <div className="sm:max-w-lg sm:mx-auto sm:w-full">
            <h2 className="font-semibold leading-9 m-6 ml-0 text-1.46x1 text-black">
              {acceptTermsAndConditionsMessages.heading}
            </h2>
            {terms
              .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
              .map(term => (
                <div key={term.code} className="flex items-start py-4">
                  <div className="flex items-center flex-shrink-0">
                    <input
                      data-browsing-ignore
                      id={term.code ?? ''}
                      type="checkbox"
                      className="form-checkbox h-6 w-6 text-indigo-600 transition duration-150 ease-in-out rounded-md"
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        if (event.target.checked) {
                          setState(prevState => ({
                            ...prevState,
                            acceptedTerms: [
                              ...(prevState.acceptedTerms ?? []),
                              term.code ?? '',
                            ],
                          }));
                        } else {
                          setState(prevState => ({
                            ...prevState,
                            acceptedTerms:
                              prevState.acceptedTerms?.filter(
                                t => t !== term.code,
                              ) ?? [],
                          }));
                        }
                      }}
                    />
                  </div>
                  <div className="ml-4 text-sm leading-5">
                    <Markdown styles={TERMS_ONBOARDING_MARKDOWN_STYLES}>
                      {(term.oneLineDescription || term.description) ?? ''}
                    </Markdown>
                  </div>
                </div>
              ))}
            <div className="mt-6">
              <span className="block w-full rounded-md">
                <Button
                  loading={acceptTermsResult?.isLoading}
                  onClick={onContinuePress}
                  disabled={!isStateValid()}
                  w-full
                  flex
                  justify-center
                  py-2
                  px-4
                  border
                  border-transparent
                  text-sm
                  font-normal
                  rounded-md
                  text-white
                  transition
                  duration-150
                  ease-in-out
                  bg-vivid-blue
                  type="submit">
                  {acceptTermsAndConditionsMessages.acceptAndContinueButton}
                </Button>
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
